import { z } from "zod";

// =============================================================================
// Reload analysis (per connection)
// Qlik's tenant-wide reload history (`GET /api/v1/reloads`) collected into the
// SenseGuard DB, plus the live queue read straight from Qlik.
// =============================================================================

// Qlik's reload `status`, kept raw: an unknown future value must not break a
// collection. These are the known ones the UI translates.
export const QLIK_RELOAD_STATUSES = [
  "QUEUED",
  "RELOADING",
  "CANCELING",
  "SUCCEEDED",
  "FAILED",
  "CANCELED",
  "EXCEEDED_LIMIT",
] as const;

// Why the last collection did not complete: `forbidden` = the connection's
// identity cannot list the tenant's reloads (needs TenantAdmin).
export const reloadCollectionErrorSchema = z.enum(["forbidden", "failed"]);
export type ReloadCollectionError = z.infer<typeof reloadCollectionErrorSchema>;

// --- Job payload (worker) ---
export const collectReloadsPayloadSchema = z.object({
  connectionId: z.string().uuid(),
  // The worker pages newest first and stops after the page that crosses this
  // instant; null = everything Qlik still keeps (~90 days).
  since: z.string().datetime().nullable(),
});
export type CollectReloadsPayload = z.infer<typeof collectReloadsPayloadSchema>;

// --- Collection state ---
export const reloadCollectionSchema = z.object({
  enabled: z.boolean(),
  // a collect_reloads job is pending or processing
  running: z.boolean(),
  lastCollectedAt: z.string().nullable(),
  lastError: reloadCollectionErrorSchema.nullable(),
  oldestReloadAt: z.string().nullable(),
  reloadCount: z.number(),
});
export type ReloadCollection = z.infer<typeof reloadCollectionSchema>;

export const reloadCollectionResponseSchema = z.object({ data: reloadCollectionSchema });
export type ReloadCollectionResponse = z.infer<typeof reloadCollectionResponseSchema>;

export const setReloadCollectionInputSchema = z.object({ enabled: z.boolean() });
export type SetReloadCollectionInput = z.infer<typeof setReloadCollectionInputSchema>;

// --- Live queue ---
export const liveReloadSchema = z.object({
  reloadId: z.string(),
  appId: z.string(),
  appName: z.string().nullable(),
  status: z.string(),
  type: z.string(),
  creationTime: z.string(),
  startTime: z.string().nullable(),
  // median of the app's succeeded reloads in the last 30 days; null = no history
  expectedSeconds: z.number().nullable(),
});
export type LiveReload = z.infer<typeof liveReloadSchema>;

export const liveReloadsResponseSchema = z.object({
  data: z.array(liveReloadSchema),
  fetchedAt: z.string(),
});
export type LiveReloadsResponse = z.infer<typeof liveReloadsResponseSchema>;

// --- Period query (errors, bottlenecks, apps) ---
// `tz` buckets hours and weekdays in the viewer's zone; an unknown zone is
// rejected here rather than reaching Postgres. `type` narrows to one origin
// (Qlik's reload `type`: automations, chronos, hub, ...).
export const reloadPeriodQuerySchema = z
  .object({
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    type: z.string().trim().min(1).max(40).optional(),
    tz: z
      .string()
      .max(64)
      .refine((tz) => {
        try {
          new Intl.DateTimeFormat("en", { timeZone: tz });
          return true;
        } catch {
          return false;
        }
      }, "Unknown time zone")
      .default("UTC"),
  })
  .passthrough();
export type ReloadPeriodQuery = z.infer<typeof reloadPeriodQuerySchema>;

const appRefSchema = z.object({ appId: z.string(), appName: z.string().nullable() });

// --- Errors ---
export const reloadAppErrorsSchema = appRefSchema.extend({
  // finished reloads in the period (succeeded + failed); failed counts
  // FAILED and EXCEEDED_LIMIT
  total: z.number(),
  failed: z.number(),
  // consecutive failures since the app's last success, whole history
  currentStreak: z.number(),
  lastStatus: z.string(),
  lastReloadAt: z.string(),
  lastSuccessAt: z.string().nullable(),
  // the app's most recent failure in the period
  lastErrorCode: z.string().nullable(),
  lastErrorMessage: z.string().nullable(),
});
export type ReloadAppErrors = z.infer<typeof reloadAppErrorsSchema>;

// Failures grouped by code + message with the digits blanked out, so a file
// name carrying a date does not split one cause into one group per day.
export const reloadErrorGroupSchema = z.object({
  errorCode: z.string().nullable(),
  // the most recent raw message of the group
  message: z.string().nullable(),
  count: z.number(),
  appCount: z.number(),
  // up to 5, most occurrences first
  apps: z.array(appRefSchema.extend({ count: z.number(), lastAt: z.string() })),
  firstAt: z.string(),
  lastAt: z.string(),
});
export type ReloadErrorGroup = z.infer<typeof reloadErrorGroupSchema>;

export const reloadErrorsResponseSchema = z.object({
  data: z.object({
    total: z.number(),
    failed: z.number(),
    apps: z.array(reloadAppErrorsSchema),
    groups: z.array(reloadErrorGroupSchema),
  }),
});
export type ReloadErrorsResponse = z.infer<typeof reloadErrorsResponseSchema>;

// --- One app's runs (the app's drawer) ---
export const reloadRunSchema = z.object({
  reloadId: z.string(),
  status: z.string(),
  type: z.string(),
  partial: z.boolean(),
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
  creationTime: z.string(),
  startTime: z.string().nullable(),
  endTime: z.string().nullable(),
});
export type ReloadRun = z.infer<typeof reloadRunSchema>;

export const reloadAppRunsResponseSchema = z.object({
  data: z.object({
    app: appRefSchema,
    summary: z.object({
      // finished reloads in the period (succeeded + failed)
      total: z.number(),
      failed: z.number(),
      // consecutive failures since the app's last success, whole history
      currentStreak: z.number(),
      lastSuccessAt: z.string().nullable(),
      // durations of succeeded reloads; wait = creation to start
      medianSeconds: z.number().nullable(),
      p90Seconds: z.number().nullable(),
      medianWaitSeconds: z.number().nullable(),
    }),
    // newest first, at most RELOAD_APP_RUNS_LIMIT
    runs: z.array(reloadRunSchema),
    // the period had more runs than the list carries
    truncated: z.boolean(),
  }),
});
export type ReloadAppRunsResponse = z.infer<typeof reloadAppRunsResponseSchema>;

export const RELOAD_APP_RUNS_LIMIT = 500;

// The reload's script log, read live from Qlik (never stored).
export const reloadLogResponseSchema = z.object({ data: z.object({ log: z.string() }) });
export type ReloadLogResponse = z.infer<typeof reloadLogResponseSchema>;

// Why a reload's log cannot be shown: Qlik dropped it (it keeps about the
// last 100 reloads per app), the connection's identity is not a member of the
// app's space, or it is over the size the worker reads.
export const RELOAD_LOG_ERROR_CODES = {
  unavailable: "RELOAD_LOG_UNAVAILABLE",
  forbidden: "RELOAD_LOG_FORBIDDEN",
  tooLarge: "RELOAD_LOG_TOO_LARGE",
} as const;

// --- Bottlenecks ---
export const reloadWaitCellSchema = z.object({
  weekday: z.number(), // 0 = Sunday
  hour: z.number(),
  count: z.number(),
  medianWaitSeconds: z.number(),
  p90WaitSeconds: z.number(),
});
export type ReloadWaitCell = z.infer<typeof reloadWaitCellSchema>;

// A minute of the day where reloads pile up (schedules set to the same time).
export const reloadCrowdedSlotSchema = z.object({
  time: z.string(), // HH:MM in `tz`
  count: z.number(),
  perDay: z.number(),
  medianWaitSeconds: z.number(),
});
export type ReloadCrowdedSlot = z.infer<typeof reloadCrowdedSlotSchema>;

export const reloadBottlenecksResponseSchema = z.object({
  data: z.object({
    medianWaitSeconds: z.number().nullable(),
    p90WaitSeconds: z.number().nullable(),
    peak: z.object({ concurrency: z.number(), at: z.string() }).nullable(),
    waitByHour: z.array(reloadWaitCellSchema),
    crowdedSlots: z.array(reloadCrowdedSlotSchema),
  }),
});
export type ReloadBottlenecksResponse = z.infer<typeof reloadBottlenecksResponseSchema>;

// --- Apps ---
export const reloadAppStatsSchema = appRefSchema.extend({
  runs: z.number(),
  perDay: z.number(),
  failed: z.number(),
  // durations of succeeded reloads
  medianSeconds: z.number().nullable(),
  p90Seconds: z.number().nullable(),
  // every finished reload, failed ones included
  totalSeconds: z.number(),
  // last 7 days' median vs the rest of the period; null = too few samples
  trendPct: z.number().nullable(),
  // creation to start, every started reload
  medianWaitSeconds: z.number().nullable(),
  lastStatus: z.string(),
  lastReloadAt: z.string(),
});
export type ReloadAppStats = z.infer<typeof reloadAppStatsSchema>;

export const reloadAppsResponseSchema = z.object({ data: z.array(reloadAppStatsSchema) });
export type ReloadAppsResponse = z.infer<typeof reloadAppsResponseSchema>;

// --- Performance ---
// Hours count every finished reload (failures included); durations only the
// succeeded ones; wait = creation to start.
export const reloadPeriodSummarySchema = z.object({
  totalSeconds: z.number(),
  finished: z.number(),
  succeeded: z.number(),
  failed: z.number(),
  medianSeconds: z.number().nullable(),
  p90Seconds: z.number().nullable(),
  medianWaitSeconds: z.number().nullable(),
});
export type ReloadPeriodSummary = z.infer<typeof reloadPeriodSummarySchema>;

export const reloadPerformanceDaySchema = z.object({
  day: z.string(), // YYYY-MM-DD in `tz`
  succeededSeconds: z.number(),
  failedSeconds: z.number(),
  count: z.number(),
  failed: z.number(),
  medianSeconds: z.number().nullable(),
  p90Seconds: z.number().nullable(),
});
export type ReloadPerformanceDay = z.infer<typeof reloadPerformanceDaySchema>;

export const reloadPerformanceResponseSchema = z.object({
  data: z.object({
    summary: reloadPeriodSummarySchema,
    // the window of the same length right before the period; null when the
    // collected history does not cover all of it
    previous: reloadPeriodSummarySchema.nullable(),
    // every day of the period, empty ones included
    days: z.array(reloadPerformanceDaySchema),
  }),
});
export type ReloadPerformanceResponse = z.infer<typeof reloadPerformanceResponseSchema>;

// --- Alerts (as of now, not period-bound) ---
export const reloadAlertSchema = z.discriminatedUnion("kind", [
  appRefSchema.extend({
    kind: z.literal("slower"),
    recentMedianSeconds: z.number(),
    baselineMedianSeconds: z.number(),
    recentSamples: z.number(),
  }),
  appRefSchema.extend({
    kind: z.literal("stopped"),
    lastReloadAt: z.string(),
    typicalIntervalSeconds: z.number(),
  }),
  appRefSchema.extend({
    kind: z.literal("failing"),
    streak: z.number(),
    since: z.string(),
    errorCode: z.string().nullable(),
  }),
]);
export type ReloadAlert = z.infer<typeof reloadAlertSchema>;

export const reloadAlertsResponseSchema = z.object({ data: z.array(reloadAlertSchema) });
export type ReloadAlertsResponse = z.infer<typeof reloadAlertsResponseSchema>;

// --- Worker -> API callback payloads (WORKER_SECRET routes) ---
export const qlikReloadRowSchema = z.object({
  reloadId: z.string().min(1),
  appId: z.string().min(1),
  type: z.string(),
  status: z.string(),
  partial: z.boolean(),
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
  creationTime: z.string().datetime(),
  startTime: z.string().datetime().nullable(),
  endTime: z.string().datetime().nullable(),
});
export type QlikReloadRow = z.infer<typeof qlikReloadRowSchema>;

export const saveQlikReloadsSchema = z.object({
  organizationId: z.string(),
  connectionId: z.string().uuid(),
  rows: z.array(qlikReloadRowSchema).max(500),
});
export type SaveQlikReloads = z.infer<typeof saveQlikReloadsSchema>;

export const saveReloadAppsSchema = z.object({
  organizationId: z.string(),
  connectionId: z.string().uuid(),
  apps: z.array(z.object({ appId: z.string().min(1), name: z.string() })).max(500),
});
export type SaveReloadApps = z.infer<typeof saveReloadAppsSchema>;

export const finalizeReloadCollectionSchema = z.object({
  organizationId: z.string(),
  connectionId: z.string().uuid(),
  jobId: z.string().uuid(),
  status: z.enum(["completed", "failed"]),
  error: reloadCollectionErrorSchema.optional(),
});
export type FinalizeReloadCollection = z.infer<typeof finalizeReloadCollectionSchema>;

// Worker `POST /qlik/reloads/active` row (live queue, straight from Qlik).
export const activeQlikReloadSchema = qlikReloadRowSchema.pick({
  reloadId: true,
  appId: true,
  type: true,
  status: true,
  creationTime: true,
  startTime: true,
});
export type ActiveQlikReload = z.infer<typeof activeQlikReloadSchema>;
